<template>
    <div class="product-page mt-3">
        <div class="section">
            <div class="container">
                <div class="loading" v-if="loading" role="status" aria-live="polite"></div>

                <nav aria-label="Brotkrümelnavigation" v-if="product">
                    <ol class="breadcrumb">
                        <li class="breadcrumb-item">
                            <router-link to="/">{{ $t('links.home') }}</router-link>
                        </li>
                        <li class="breadcrumb-item" v-if="hasSingleArtistid">
                            <router-link :to="'/artist/' + encodeURIComponent(firstArtistid.artistnatuerlich) + '/' + firstArtistid.artistid"
                                         :aria-label="'Gehe zu Künstler: ' + firstArtistid.artistnatuerlich">
                                {{ firstArtistid.artistnatuerlich }}
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active" aria-current="page">{{ product.title }}</li>
                    </ol>
                </nav>

                <div v-if="product" class="main main-raised main-product" style="margin: 0">
                    <div class="audio-player mt-lg-3">
                        <div class="row effect-wrap">
                            <div class="col-12 col-lg-6 left-effect d-flex flex-row align-items-center"
                                 v-if="mediumCover">
                                <div class="tab-content m-0 mt-lg-2 mb-lg-2">
                                    <div class="tab-pane active" id="product-page2">
                                        <img :src="mediumCover"
                                             class="img-raised rounded img-fluid album-art mb-1 prevent-download"
                                             :alt="'Coverbild für ' + product.configuration.name + ' von ' + normalizedArtist + ' mit dem Titel ' + product.title"
                                             role="img" />
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-lg-6">
                                <h2 class="title">
                                    <span>{{ normalizedArtist }}</span>
                                </h2>
                                <h3 class="main-price">{{ product.title }}</h3>

                                <dl class="row">
                                    <dt class="col-sm-3">{{ $t('product.release_date') }}</dt>
                                    <dd class="col-sm-9">{{ dateFormat(product.physical_release_date) }}</dd>

                                    <dt class="col-sm-3">{{ $t('product.gtin') }}</dt>
                                    <dd class="col-sm-9">{{ product.gtin }}</dd>

                                    <dt class="col-sm-3">{{ $t('product.label') }}</dt>
                                    <dd class="col-sm-9">{{ product.label_name }}</dd>

                                    <dt class="col-sm-3">{{ $t('product.distributor') }}</dt>
                                    <dd class="col-sm-9">{{ product.distributor.name }}</dd>

                                    <dt class="col-sm-3">{{ $t('product.genre') }}</dt>
                                    <dd class="col-sm-9">{{ product.genre.name }}</dd>

                                    <dt class="col-sm-3">{{ $t('product.configuration') }}</dt>
                                    <dd class="col-sm-9">{{ product.configuration.name }}</dd>

                                    <dt class="col-sm-3">{{ $t('product.set_total') }}</dt>
                                    <dd class="col-sm-9">{{ product.set_total }}</dd>

                                    <template v-if="hasArtistIds">
                                        <dt class="col-sm-3">{{ $t('product.multiple_artists') }}</dt>
                                        <dd class="col-sm-9">
                                            <template v-for="(anArtistid, index) in productArtistsids">
                                                <router-link v-if="anArtistid.artistid.artistid"
                                                             :to="'/artist/' + encodeURIComponent(anArtistid.artistid.artistnatuerlich) + '/' + anArtistid.artistid.artistid"
                                                             :aria-label="'Gehe zu Künstler: ' + anArtistid.artistid.artistnatuerlich">
                                                    {{ anArtistid.artistid.artistnatuerlich }}
                                                </router-link>
                                                <span v-else>{{ anArtistid.artistid.artistnatuerlich }}</span>{{ ((index + 1) < productArtistsids.length) ? ', ' : '' }}
                                            </template>
                                        </dd>
                                    </template>
                                </dl>
                            </div>
                        </div>

                        <div class="row mt-lg-3" v-if="hasTracksWithSoundFiles">
                            <div class="col-sm-12 col-lg-10 offset-lg-1 meta-container">
                                <div class="text-center lead no-wrap mb-2 mb-lg-0" role="region" aria-label="Aktueller Titel">
                                    &raquo;
                                    <span class="title normal-wrap" role="status" aria-live="polite" amplitude-song-info="name" amplitude-main-song-info="true"></span>
                                    &laquo;
                                </div>
                                <div class="time-container">
                                    <div class="current-time" aria-live="polite" aria-label="Aktuelle Spielzeit">
                                        <span class="amplitude-current-minutes" amplitude-main-current-minutes="true"></span>:<span class="amplitude-current-seconds" amplitude-main-current-seconds="true"></span>
                                    </div>
                                    <div class="duration" aria-label="Gesamtdauer des Titels">
                                        <span class="amplitude-duration-minutes" amplitude-main-duration-minutes="true">3</span>:<span class="amplitude-duration-seconds" amplitude-main-duration-seconds="true">30</span>
                                    </div>
                                </div>
                                <input type="range"
                                       class="amplitude-song-slider"
                                       role="slider"
                                       aria-label="Song-Position"
                                       amplitude-main-song-slider="true"
                                       step=".1" />
                                <div class="control-container">
                                    <div class="amplitude-prev" role="button" tabindex="0" aria-label="Vorheriger Titel"></div>
                                    <span class="button amplitude-play-pause" role="button" tabindex="0" aria-label="Titel abspielen oder pausieren" amplitude-main-play-pause="true"></span>
                                    <div class="amplitude-next" role="button" tabindex="0" aria-label="Nächster Titel"></div>
                                </div>
                            </div>
                        </div>

                        <div class="row mt-3" v-if="hasTracks">
                            <table class="table table-striped playlist" aria-label="Trackliste">
                                <thead>
                                    <tr>
                                        <th scope="col" width="60" class="text-center">#</th>
                                        <th width="10"></th>
                                        <th scope="col">{{ $t('product.tracks.title') }}</th>
                                        <th scope="col" class="text-center">{{ $t('product.tracks.duration') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="track in tracks"
                                        :class="[hasSoundFile(track) ? 'song amplitude-song-container amplitude-play-pause' : '']"
                                        :key="track.number"
                                        :amplitude-song-index="track.songIndex">
                                        <td class="text-center">{{ track.number }}</td>
                                        <td>
                                            <div class="song-now-playing-icon-container" v-if="hasSoundFile(track)">
                                                <div class="play-button-container" aria-hidden="true"></div>
                                                <img class="now-playing" :src="nowPlaying" alt="Jetzt spielender Titel" />
                                            </div>
                                        </td>
                                        <td>
                                            <span class="song-work" v-if="track.work">{{ track.work }}: </span><span class="song-title">{{ track.title }}</span>
                                        </td>
                                        <td class="song-duration text-center">
                                            {{ track.duration }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DataService from './../services/DataService'
import moment from 'moment'
import { logger } from './../util/Logger'
import Amplitude from 'amplitudejs'
import isString from 'lodash-es/isString'
import replace from 'lodash-es/replace'
import TrackParser from './../util/TrackParser'
import nowPlaying from './../assets/img/audioplayer/now-playing.svg'

export default {
  name: 'ProductDetailComponent',
  props: ['ean'],
  data () {
    return {
      nowPlaying,
      product: null,
      productArtistsids: [],
      tracks: null,
      loading: false,
      artistRegExp: {
        commaRegExp: /([^,\r\n\t\f\v ])(,)([^,\r\n\t\f\v ])/gi,
        commaSubst: '$1, $3',
        slashRegexp: /([^/\r\n\t\f\v ])(\/)([^/\r\n\t\f\v ])/gi,
        slashSubst: '$1 / $3'
      }
    }
  },
  created () {
    this.getProduct()
  },
  updated () {
    if (this.hasTracksWithSoundFiles) {
      this.initPlayer()
    }
  },
  destroyed () {
    Amplitude.pause()
  },
  watch: {
    // call again the method if the route changes
    $route: 'getProduct'
  },
  computed: {
    normalizedArtist () {
      if (this.product.artist) {
        let normalizedArtist = replace(this.product.artist, this.artistRegExp.commaRegExp, this.artistRegExp.commaSubst)
        normalizedArtist = replace(normalizedArtist, this.artistRegExp.slashRegexp, this.artistRegExp.slashSubst)
        return normalizedArtist
      }
      return this.product.artist
    },
    cover () {
      try {
        if (isString(this.product.artworks.artwork[0].url)) {
          return this.product.artworks.artwork[0].url
        }
      } catch (err) {
        return null
      }
    },
    /**
             * currently only the 80px are returned within the api,
             * so we just replace the end of the url
             * @return {string|null}
             */
    mediumCover () {
      if (isString(this.cover)) {
        return this.cover.replace('0080px', '0300px')
      }
      return null
    },
    tracksAsSongs () {
      if (!this.hasTracks) {
        return []
      }
      const songs = []
      for (const track of this.tracks) {
        if (!track.soundUrl) {
          continue
        }
        songs.push({
          name: track.title,
          artist: this.product.artist,
          album: this.product.title,
          url: track.soundUrl
        })
      }
      return songs
    },
    hasTracks () {
      return this.tracks.length > 0
    },
    hasTracksWithSoundFiles () {
      if (this.hasTracks) {
        for (const track of this.tracks) {
          if (this.hasSoundFile(track)) return true
        }
      }
      return false
    },
    hasSingleArtistid () {
      return this.productArtistsids.length === 1
    },
    firstArtistid () {
      if (this.productArtistsids.length === 1) {
        return this.productArtistsids[0].artistid
      }
      return null
    },
    hasArtistIds () {
      return this.productArtistsids.length > 0
    }
  },
  methods: {
    getProduct () {
      this.loading = true
      DataService.getProduct(this.ean)
        .then((data) => {
          if (data.catalog.length === 0) {
            throw new Error('no product-data found for ean ' + this.ean)
          }

          this.product = data.catalog[0]
          if (this.product && this.product.artistids) {
            this.productArtistsids = this.product.artistids
          }

          if (!this.product || !this.product.tracks) {
            this.tracks = []
          } else {
            this.tracks = TrackParser.parse(this.product.tracks.track)
          }
        })
        .catch((jqXHR, textStatus, errorThrown) => {
          this.tracks = [] // prevent tracks not found error
          logger.error('Error loading product: ', jqXHR, textStatus, errorThrown)
          this.$router.replace('/404')
        })
        .finally(() => {
          this.loading = false
        })
    },
    dateFormat (date) {
      return moment.utc(date, 'YYYY-MM-DD').format('DD.MM.YYYY')
    },
    hasSoundFile (track) {
      return track.soundUrl && track.soundUrl.length > 0
    },
    initPlayer () {
      // init player for all songs
      Amplitude.init({
        songs: this.tracksAsSongs
      })
    }
  }
}
</script>
